import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LibraryPanel from '../components/tutor/structure/LibraryPanel';
import CourseTreePanel from '../components/tutor/structure/CourseTreePanel';
import VersionPanel from '../components/tutor/structure/VersionPanel';
import AiStructurePanel from '../components/tutor/structure/AiStructurePanel';
import { TutorStructureNode, getStructure } from '../services/tutorStructureService';
import { getCourse, TutorCourse } from '../services/tutorCourseService';

export default function TutorCourseStructure() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const id = Number(courseId);

  const [nodes, setNodes] = useState<TutorStructureNode[]>([]);
  const [course, setCourse] = useState<TutorCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [versionRefreshKey, setVersionRefreshKey] = useState(0);
  const [aiAppliedKey, setAiAppliedKey] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([getStructure(id), getCourse(id)])
      .then(([structureData, courseData]) => {
        setNodes(structureData.nodes);
        setCourse(courseData);
        setError(null);
      })
      .catch((err) => {
        if (err?.response?.status === 403) {
          setError('You do not have access to this course.');
        } else if (err?.response?.status === 404) {
          setError('Course not found.');
          setTimeout(() => navigate('/tutor/courses'), 2000);
        } else {
          setError('Failed to load course structure. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  }, [id, aiAppliedKey]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tutor/courses')} sx={{ mt: 2 }}>
          Back to courses
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => navigate('/tutor/courses')}>
          My Courses
        </Link>
        {course && (
          <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/tutor/courses/${id}`)}>
            {course.name}
          </Link>
        )}
        <Typography color="text.primary">Topic Structure</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {course ? course.name : 'Course Topic Structure'}
          </Typography>
          {course && (
            <Typography variant="body2" color="text.secondary">
              {course.subjectName ?? course.board ?? ''}
              {course.status === 'DRAFT' && (
                <Box component="span" sx={{ ml: 1, px: 1, py: 0.25, bgcolor: 'warning.light', borderRadius: 1, fontSize: '0.75rem', color: 'warning.dark' }}>
                  DRAFT
                </Box>
              )}
            </Typography>
          )}
        </Box>
        <Button size="small" variant="outlined" onClick={() => navigate(`/tutor/courses/${id}/codes`)}>
          Registration Codes
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drag topics from the library into your course structure, or add custom topics and sections.
        Changes are saved automatically.
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2} sx={{ height: 'calc(100vh - 220px)' }}>
        <Grid item xs={12} md={2}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <LibraryPanel courseId={id} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CourseTreePanel
              courseId={id}
              nodes={nodes}
              onNodesChange={setNodes}
              onVersionPublished={() => setVersionRefreshKey((k) => k + 1)}
              onContentEdit={(nodeId) => navigate(`/tutor/courses/${id}/nodes/${nodeId}/content`)}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            <AiStructurePanel
              courseId={id}
              onApplied={() => setAiAppliedKey((k) => k + 1)}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={2}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            <VersionPanel courseId={id} refreshKey={versionRefreshKey} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
